import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import VideoCreativeService from 'services/VideoCreativeService';
import { CIAdDetail, SimilarAdResult, SimilarSegmentResult } from './types';
import { formatEnumLabel } from './adColumns';

const service = new VideoCreativeService();

function CollapsibleSection({ title, children, defaultOpen = true, action }: { title: string; children: React.ReactNode; defaultOpen?: boolean; action?: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setOpen(!open)}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {action}
          <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
}

interface ExtractionDetailProps {
  detail: CIAdDetail;
  /** Verbatim transcript (one Whisper segment per line). When provided, the
   *  beat and segment renderers slice this text by lineStart/lineEnd to show
   *  the actual spoken script for each item — making the citation between a
   *  beat and its transcript lines visible inline (no tab-switching). */
  transcript?: string | null;
  onCopy?: (text: string, label: string) => void;
  /** `preview` (inline row expand) shows keyframes + features side by side and
   *  omits Persuasion Beats and Analysis Text — those are reserved for the
   *  full detail modal. `full` (default) stacks every section. */
  variant?: 'full' | 'preview';
}

/** Slice a 1-indexed inclusive line range out of a transcript string. */
function sliceLines(transcript: string | null | undefined, lineStart: number | null, lineEnd: number | null): string | null {
  if (!transcript || lineStart == null) return null;
  const lines = transcript.split(/\r?\n/);
  const start = Math.max(1, lineStart);
  const end = lineEnd != null ? Math.max(start, lineEnd) : start;
  return lines.slice(start - 1, end).join('\n').trim() || null;
}

export default function ExtractionDetail({ detail, transcript, onCopy, variant = 'full' }: ExtractionDetailProps) {
  const [similarAds, setSimilarAds] = useState<SimilarAdResult[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [showSimilar, setShowSimilar] = useState(false);
  const [similarSegments, setSimilarSegments] = useState<Record<string, SimilarSegmentResult[]>>({});
  const [segSimilarLoading, setSegSimilarLoading] = useState<string | null>(null);

  const isPreview = variant === 'preview';

  const handleFindSimilar = async () => {
    if (showSimilar) { setShowSimilar(false); return; }
    setSimilarLoading(true);
    const result = await service.findSimilarAds(detail.adId);
    if (result?.similar) setSimilarAds(result.similar);
    setShowSimilar(true);
    setSimilarLoading(false);
  };

  const handleFindSimilarSegment = async (segmentId: string, role: string | null) => {
    if (similarSegments[segmentId]) {
      setSimilarSegments(prev => { const next = { ...prev }; delete next[segmentId]; return next; });
      return;
    }
    setSegSimilarLoading(segmentId);
    const result = await service.findSimilarSegments(segmentId, 5, role || undefined);
    if (result?.similar) setSimilarSegments(prev => ({ ...prev, [segmentId]: result.similar }));
    setSegSimilarLoading(null);
  };

  // Keyframes used to live here; they now live at the top of the Transcript
  // tab so per-line timestamps can be matched against them.

  const featuresSection = detail.features && (
    <CollapsibleSection title="Features" action={
      <Button
        size="small"
        variant={showSimilar ? 'contained' : 'outlined'}
        onClick={(e) => { e.stopPropagation(); handleFindSimilar(); }}
        disabled={similarLoading}
        sx={{ mr: 1 }}
      >
        {similarLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
        {showSimilar ? 'Hide Similar' : 'Find Similar Ads'}
      </Button>
    }>
      {/* Similar ads panel */}
      {showSimilar && similarAds.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Similar Ads</Typography>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell width="22%">Page</TableCell>
                <TableCell width="28%">Headline</TableCell>
                <TableCell width="12%">Match</TableCell>
                <TableCell width="14%">Hook</TableCell>
                <TableCell width="12%">Tone</TableCell>
                <TableCell width="12%">Format</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {similarAds.map(sa => (
                <TableRow key={sa.adId} hover sx={{ cursor: 'pointer' }}
                  onClick={() => { navigator.clipboard.writeText(sa.adId); onCopy?.(sa.adId, `ad ID (${sa.pageName || sa.adId.slice(0, 8)})`); }}>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sa.pageName || '-'}
                  </TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sa.headline || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={`${Math.round(sa.score * 100)}%`} size="small"
                      color={sa.score > 0.85 ? 'success' : sa.score > 0.7 ? 'info' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatEnumLabel(sa.hookDevice)}</TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatEnumLabel(sa.tone)}</TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatEnumLabel(sa.productionFormat)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      {showSimilar && similarAds.length === 0 && !similarLoading && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary">No similar ads found. Embeddings may not be generated yet.</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
        {([
          ['Awareness', detail.features.awarenessLevel],
          ['Hook Device', detail.features.hookDevice],
          ['Persuasion Angle', detail.features.persuasionAngle],
          ['Proof Type', detail.features.proofType],
          ['Narrative Structure', detail.features.narrativeStructure],
          ['Emotion', detail.features.primaryEmotion],
          ['Presenter', detail.features.presenter],
          ['Production Format', detail.features.productionFormat],
          ['Tone', detail.features.tone],
          ['Offer Type', detail.features.offerType],
          ['CTA Type', detail.features.ctaType],
          ['Segments', detail.features.segmentCount != null ? String(detail.features.segmentCount) : null],
        ] as [string, string | null][]).map(([label, val]) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2">{formatEnumLabel(val)}</Typography>
          </Box>
        ))}
      </Box>
      {/* Free-text summaries */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {([
          ['Hook Summary', detail.features.hookSummary],
          ['Problem Summary', detail.features.problemSummary],
          ['Core Benefit', detail.features.coreBenefit],
          ['Unique Angle', detail.features.uniqueAngle],
          ['Strongest Moment', detail.features.strongestMomentSummary],
        ] as [string, string | null][]).filter(([, val]) => val).map(([label, val]) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2">{val}</Typography>
          </Box>
        ))}
      </Box>
    </CollapsibleSection>
  );

  const segmentsSection = detail.segments && detail.segments.length > 0 && (
    <CollapsibleSection title="Ad Segments">
      {detail.segments.map(seg => (
        <Box key={seg.segmentId} sx={{ mb: 1.5, pl: 1, borderLeft: '3px solid #2e7d32' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>{seg.segmentOrder}.</Typography>
            <Chip label={formatEnumLabel(seg.segmentRole)} size="small" color="success" variant="outlined" />
            {seg.startTime != null && seg.endTime != null && (
              <Typography variant="caption" color="text.secondary">
                {seg.startTime.toFixed(1)}s &ndash; {seg.endTime.toFixed(1)}s
              </Typography>
            )}
            {seg.confidence != null && (
              <Typography variant="caption" color="text.secondary">
                ({Math.round(seg.confidence * 100)}%)
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">{seg.summary}</Typography>
          {(() => {
            const sliced = sliceLines(transcript, seg.lineStart, seg.lineEnd);
            const text = sliced || seg.transcriptExcerpt;
            const range = seg.lineStart != null
              ? (seg.lineEnd != null && seg.lineEnd !== seg.lineStart
                  ? `lines ${seg.lineStart}–${seg.lineEnd}`
                  : `line ${seg.lineStart}`)
              : null;
            if (!text) return null;
            return (
              <Box sx={{ mt: 0.5 }}>
                {range && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {range}{sliced ? '' : ' (excerpt — transcript unavailable)'}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', whiteSpace: 'pre-wrap' }}>
                  &ldquo;{text}&rdquo;
                </Typography>
              </Box>
            );
          })()}
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            {seg.emotionalTone && <Typography variant="caption" color="text.secondary">Tone: {formatEnumLabel(seg.emotionalTone)}</Typography>}
            {seg.pacing && <Typography variant="caption" color="text.secondary">Pacing: {formatEnumLabel(seg.pacing)}</Typography>}
            {seg.proofType && <Typography variant="caption" color="text.secondary">Proof: {formatEnumLabel(seg.proofType)}</Typography>}
            {seg.ctaType && <Typography variant="caption" color="text.secondary">CTA: {formatEnumLabel(seg.ctaType)}</Typography>}
            {seg.shotType && <Typography variant="caption" color="text.secondary">Shot: {seg.shotType}</Typography>}
            <Button
              size="small" variant="text"
              sx={{ fontSize: '0.7rem', minWidth: 0, p: 0, ml: 'auto' }}
              disabled={segSimilarLoading === seg.segmentId}
              onClick={() => handleFindSimilarSegment(seg.segmentId, seg.segmentRole)}
            >
              {segSimilarLoading === seg.segmentId ? '...' : similarSegments[seg.segmentId] ? 'hide similar' : 'find similar'}
            </Button>
          </Box>
          {similarSegments[seg.segmentId] && (
            <Box sx={{ mt: 0.5, ml: 2, pl: 1, borderLeft: '2px dashed #ccc' }}>
              {similarSegments[seg.segmentId].map(ss => (
                <Box key={ss.segmentId} sx={{ mb: 0.5 }}>
                  <Typography variant="caption">
                    <Chip label={`${Math.round(ss.score * 100)}%`} size="small" sx={{ mr: 0.5 }} />
                    {ss.summary || '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </CollapsibleSection>
  );

  const beatsSection = detail.beats.length > 0 && (
    <CollapsibleSection title="Persuasion Beats">
      {detail.beats.map(beat => {
        const lineRangeLabel = beat.lineStart != null
          ? (beat.lineEnd != null && beat.lineEnd !== beat.lineStart
              ? `lines ${beat.lineStart}–${beat.lineEnd}`
              : `line ${beat.lineStart}`)
          : null;
        const scriptSlice = sliceLines(transcript, beat.lineStart, beat.lineEnd);
        return (
          <Box key={beat.beatId} sx={{ mb: 1, pl: 1, borderLeft: '3px solid #1976d2' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600}>
                {beat.beatOrder}. {beat.beatName}
              </Typography>
              <Chip label={formatEnumLabel(beat.beatType)} size="small" />
              {lineRangeLabel && (
                <Typography variant="caption" color="text.secondary">{lineRangeLabel}</Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">{beat.beatDescription}</Typography>
            {scriptSlice && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                &ldquo;{scriptSlice}&rdquo;
              </Typography>
            )}
            {beat.whyItWorks && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Why it works: {beat.whyItWorks}
              </Typography>
            )}
          </Box>
        );
      })}
    </CollapsibleSection>
  );

  const analysisSection = detail.analysisText && (
    <CollapsibleSection title="Analysis Text">
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
        {detail.analysisText}
      </Typography>
    </CollapsibleSection>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isPreview ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {featuresSection && <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>{featuresSection}</Box>}
        </Box>
      ) : (
        <>{featuresSection}</>
      )}
      {segmentsSection}
      {!isPreview && beatsSection}
      {!isPreview && analysisSection}
    </Box>
  );
}
